import React, { useContext, useEffect, useMemo, useState, useRef, useCallback } from "react";
import MainButton from "../MainButton/MainButton";
import SecondButton from "../SecondButton/SecondButton";
import PropTypes from "prop-types";
import { AuthContext } from "../../../context/AuthContext";

import sortUp from "../../../assets/data/sort_up.png";
import sortDown from "../../../assets/data/sort_down.png";
import "./DataTable.css";

// Helper function to safely access nested property values from an object.
const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

export default function DataTable({
  fetchDataFunction,
  columns,
  optionalColumns = [],
  sortOptions = [],
  filterDefinitions = [],
  defaultSortBy,
  itemKey,
  customRenderers = {},
  rowActions,
  actionsColumnLabel = "Actions",
  onSelectionChange,
  showCheckboxes = false,
  showSort = true,
  showFilters = true,
  showPageSize = true,
  defaultPageSize = 10,
  tableId = "default-table",
  emptyMessage = "No results found",
}) {
  const { token } = useContext(AuthContext);

  const getCacheKey = useCallback((key) => `${tableId}-filter-${key}`, [tableId]);
  const saveToCache = useCallback(
    (key, value) => {
      localStorage.setItem(getCacheKey(key), value);
    },
    [getCacheKey],
  );
  const loadFromCache = useCallback(
    (key) => {
      return localStorage.getItem(getCacheKey(key)) || "";
    },
    [getCacheKey],
  );
  const clearCacheForKey = useCallback(
    (key) => {
      localStorage.removeItem(getCacheKey(key));
    },
    [getCacheKey],
  );

  // --- STATE MANAGEMENT ---
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortBy, setSortBy] = useState(defaultSortBy || (sortOptions?.length > 0 ? sortOptions[0].value : ""));
  const [sortOrder, setSortOrder] = useState("desc");
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredRecords, setFilteredRecords] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [filters, setFilters] = useState({});
  const [visibleOptionals, setVisibleOptionals] = useState([]);
  const [lastEditedFilterKey, setLastEditedFilterKey] = useState(null);

  const tableContainerRef = useRef(null);
  const prevVisibleOptionalsLength = useRef(0);
  const filterThRefs = useRef({});
  const scrollPositionRef = useRef(0);

  const hasOptionalColumns = optionalColumns.length > 0;
  const showColumnsRow = hasOptionalColumns || showPageSize;

  const currentTableId = tableId;

  // Generates the initial state for filters based on their definitions.
  const initialFilters = useMemo(() => {
    if (!filterDefinitions?.length) return {};
    return filterDefinitions.reduce((acc, def) => {
      const stateKey = def.filterKey || def.key;
      if (def.type === "minmax" || def.type === "date-range") {
        const minKey = def.minKey || `min_${stateKey}`;
        const maxKey = def.maxKey || `max_${stateKey}`;
        acc[minKey] = loadFromCache(minKey);
        acc[maxKey] = loadFromCache(maxKey);
      } else {
        acc[stateKey] = loadFromCache(stateKey);
      }
      return acc;
    }, {});
  }, [filterDefinitions, loadFromCache]);

  useEffect(() => {
    if (Object.keys(filters).length === 0 && Object.keys(initialFilters).length > 0) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  const visibleColumns = useMemo(
    () => [...columns, ...optionalColumns.filter((col) => visibleOptionals.includes(col.key))],
    [visibleOptionals, columns, optionalColumns],
  );

  // Memoized debounced fetch function
  const fetchData = useCallback(async () => {
    if (!token || !fetchDataFunction) return;

    if (tableContainerRef.current) {
      scrollPositionRef.current = tableContainerRef.current.scrollLeft;
    }

    setLoading(true);
    if (showCheckboxes) {
      setSelectedItems(new Set());
    }

    try {
      const data = await fetchDataFunction({
        token,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        filters,
      });
      setItems(data.items || []);
      setTotalRecords(data.total || 0);
      setFilteredRecords(data.filtered !== undefined ? data.filtered : null);
    } catch (error) {
      console.error("Error fetching data:", error);
      setItems([]);
      setTotalRecords(0);
      setFilteredRecords(null);
    } finally {
      setLoading(false);
      requestAnimationFrame(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollLeft = scrollPositionRef.current;
        }
      });
    }
  }, [token, page, pageSize, sortBy, sortOrder, filters, fetchDataFunction, showCheckboxes]);

  // Data fetching effect
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-scroll for new optional columns
  useEffect(() => {
    if (visibleOptionals.length > prevVisibleOptionalsLength.current) {
      const container = tableContainerRef.current;
      if (container) {
        container.scrollTo({
          left: container.scrollWidth,
          behavior: "smooth",
        });
      }
    }
    prevVisibleOptionalsLength.current = visibleOptionals.length;
  }, [visibleOptionals]);

  // Auto-scroll to edited filter
  useEffect(() => {
    if (lastEditedFilterKey && filterThRefs.current[lastEditedFilterKey]) {
      const container = tableContainerRef.current;
      const thElement = filterThRefs.current[lastEditedFilterKey];

      if (container && thElement) {
        const containerRect = container.getBoundingClientRect();
        const thRect = thElement.getBoundingClientRect();

        if (thRect.left < containerRect.left || thRect.right > containerRect.right) {
          container.scrollTo({
            left: thElement.offsetLeft - container.offsetLeft,
            behavior: "smooth",
          });
        }
      }
      setLastEditedFilterKey(null);
    }
  }, [lastEditedFilterKey]);

  // Notify parent on selection change
  useEffect(() => {
    if (showCheckboxes && onSelectionChange) {
      onSelectionChange(Array.from(selectedItems));
    }
  }, [selectedItems, onSelectionChange, showCheckboxes]);

  // --- HANDLER FUNCTIONS ---
  const handleSort = (colKey) => {
    const apiSortKey = sortOptions.find((opt) => opt.value.endsWith(colKey))?.value || colKey;
    if (sortBy === apiSortKey) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(apiSortKey);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const handleSelectItem = (key) => {
    setSelectedItems((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(key)) {
        newSelection.delete(key);
      } else {
        newSelection.add(key);
      }
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    const currentPageKeys = items.map((item) => getNestedValue(item, itemKey));
    const allSelectedOnPage = currentPageKeys.length > 0 && currentPageKeys.every((key) => selectedItems.has(key));

    setSelectedItems((prev) => {
      const newSelection = new Set(prev);
      if (allSelectedOnPage) {
        currentPageKeys.forEach((key) => newSelection.delete(key));
      } else {
        currentPageKeys.forEach((key) => newSelection.add(key));
      }
      return newSelection;
    });
  };

  const toggleColumn = (colKey) => {
    setVisibleOptionals((prev) => (prev.includes(colKey) ? prev.filter((c) => c !== colKey) : [...prev, colKey]));
  };

  const onFilterChange = useCallback(
    (key, value, columnKey) => {
      saveToCache(key, value);
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPage(1);
      if (columnKey) {
        setLastEditedFilterKey(columnKey);
      }
    },
    [saveToCache],
  );

  const resetFilters = useCallback(() => {
    const emptyFilters = filterDefinitions.reduce((acc, def) => {
      const stateKey = def.filterKey || def.key;
      if (def.type === "minmax" || def.type === "date-range") {
        const minKey = def.minKey || `min_${stateKey}`;
        const maxKey = def.maxKey || `max_${stateKey}`;
        acc[minKey] = "";
        acc[maxKey] = "";
      } else {
        acc[stateKey] = "";
      }
      return acc;
    }, {});

    filterDefinitions.forEach((def) => {
      const stateKey = def.filterKey || def.key;
      if (def.type === "minmax" || def.type === "date-range") {
        const minKey = def.minKey || `min_${stateKey}`;
        const maxKey = def.maxKey || `max_${stateKey}`;
        clearCacheForKey(minKey);
        clearCacheForKey(maxKey);
      } else {
        clearCacheForKey(stateKey);
      }
    });

    setFilters(emptyFilters);
    setPage(1);
  }, [filterDefinitions, clearCacheForKey]);

  useEffect(() => {
    localStorage.setItem(`${currentTableId}-pageSize`, pageSize.toString());
  }, [pageSize, currentTableId]);

  useEffect(() => {
    const cachedPageSize = localStorage.getItem(`${currentTableId}-pageSize`);
    if (cachedPageSize) {
      setPageSize(Number(cachedPageSize));
    }
  }, [currentTableId]);

  const allOnPageSelected =
    showCheckboxes && items.length > 0 && items.every((item) => selectedItems.has(getNestedValue(item, itemKey)));

  const hasRowActions = typeof rowActions === "function" || (Array.isArray(rowActions) && rowActions.length > 0);
  const totalColumns = visibleColumns.length + (hasRowActions ? 1 : 0) + (showCheckboxes ? 1 : 0);

  const renderFilterInput = (filterDef, col) => {
    const { key, type, options, inputType = "text", minKey, maxKey, filterKey } = filterDef;
    const stateKey = filterKey || key;

    if (type === "minmax" || type === "date-range") {
      const finalMinKey = minKey || `min_${stateKey}`;
      const finalMaxKey = maxKey || `max_${stateKey}`;
      return (
        <div className="dt-filter-minmax-group">
          <input
            type={type === "date-range" ? "date" : inputType}
            placeholder="Min"
            value={filters[finalMinKey] || ""}
            onChange={(e) => onFilterChange(finalMinKey, e.target.value, col.key)}
            onClick={(e) => e.stopPropagation()}
          />
          <input
            type={type === "date-range" ? "date" : inputType}
            placeholder="Max"
            value={filters[finalMaxKey] || ""}
            onChange={(e) => onFilterChange(finalMaxKey, e.target.value, col.key)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      );
    }

    if (type === "select") {
      return (
        <select
          value={filters[stateKey] || ""}
          onChange={(e) => onFilterChange(stateKey, e.target.value, col.key)}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="">All</option>
          {options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={inputType}
        value={filters[stateKey] || ""}
        onChange={(e) => onFilterChange(stateKey, e.target.value, col.key)}
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  return (
    <section className="dt-container">
      {/* Toolbar */}
      {showSort && sortOptions?.length > 0 && (
        <div className="dt-toolbar">
          <div className="dt-sort">
            <span className="dt-sort-label">Sort by:</span>
            <div className="dt-select-wrapper">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
              >
                {sortOptions?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="dt-select-wrapper">
              <select
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value);
                  setPage(1);
                }}
              >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
              </select>
            </div>
          </div>
          <div className="dt-stats">
            <span className="dt-stat">Total: {totalRecords.toLocaleString()}</span>
            {filteredRecords !== null && (
              <>
                <span className="dt-stat-sep"> </span>
                <span className="dt-stat">Filtered: {filteredRecords.toLocaleString()}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Columns & Controls Row */}
      {showColumnsRow && (
        <div className="dt-columns-row">
          {hasOptionalColumns && (
            <div className="dt-columns">
              {optionalColumns.map((col) => (
                <button
                  key={col.key}
                  type="button"
                  className={`dt-column-toggle-btn ${visibleOptionals.includes(col.key) ? "active" : ""}`}
                  onClick={() => toggleColumn(col.key)}
                  title={`Toggle ${col.label}`}
                >
                  + {col.label}
                </button>
              ))}
            </div>
          )}
          {showPageSize && (
            <div className="dt-pagesize">
              <span className="dt-pagesize-label">Rows:</span>
              <div className="dt-select-wrapper">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 20, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="dt-reset-filters-btn" onClick={resetFilters}>
                Reset Filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Main Table */}
      <div className="dt-table-container" ref={tableContainerRef}>
        <table className="dt-data-table">
          <thead>
            <tr>
              {showCheckboxes && (
                <th className="dt-checkbox-cell">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={allOnPageSelected}
                    indeterminate={!allOnPageSelected && Array.from(selectedItems).length > 0}
                    disabled={items.length === 0}
                  />
                </th>
              )}
              {visibleColumns.map((col) => {
                const isSortable = sortOptions?.some((option) => option.value.endsWith(col.key));
                const activeSortKey = sortBy.split(".").pop();
                const isActiveSort = isSortable && activeSortKey === col.key.split(".").pop();

                return (
                  <th
                    key={col.key}
                    onClick={isSortable ? () => handleSort(col.key) : undefined}
                    style={{
                      cursor: isSortable ? "pointer" : "default",
                      width: col.width || "auto",
                      minWidth: col.minWidth || "auto",
                      maxWidth: col.maxWidth || "none",
                    }}
                  >
                    <span className="dt-th-inner">
                      <span className="dt-th-label">{col.label}</span>
                      {isActiveSort && (
                        <img src={sortOrder === "asc" ? sortUp : sortDown} alt="" className="dt-sort-icon" />
                      )}
                    </span>
                  </th>
                );
              })}
              {hasRowActions && <th className="dt-actions-header">{actionsColumnLabel}</th>}
            </tr>

            {showFilters && (
              <tr className="dt-filter-row">
                {showCheckboxes && <th />}
                {visibleColumns.map((col) => {
                  const filterDef = filterDefinitions.find((f) => f.key === col.key);
                  return (
                    <th key={`${col.key}-filter`} ref={(el) => (filterThRefs.current[col.key] = el)}>
                      {filterDef ? renderFilterInput(filterDef, col) : null}
                    </th>
                  );
                })}
                {hasRowActions && <th className="dt-actions-header"></th>}
              </tr>
            )}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={totalColumns} className="dt-loading-cell">
                  <div>Loading...</div>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={totalColumns} className="dt-empty-cell">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const key = getNestedValue(item, itemKey);
                const isSelected = showCheckboxes && selectedItems.has(key);
                return (
                  <tr key={key} className={isSelected ? "dt-row-selected" : ""}>
                    {showCheckboxes && (
                      <td className="dt-checkbox-cell">
                        <input type="checkbox" checked={isSelected} onChange={() => handleSelectItem(key)} />
                      </td>
                    )}

                    {visibleColumns.map((col) => {
                      if (customRenderers?.[col.key]) {
                        return (
                          <td
                            key={col.key}
                            style={{
                              width: col.width || "auto",
                              minWidth: col.minWidth || "auto",
                              maxWidth: col.maxWidth || "none",
                            }}
                          >
                            {customRenderers[col.key](item)}
                          </td>
                        );
                      }
                      const val = getNestedValue(item, col.key);
                      return (
                        <td
                          key={col.key}
                          style={{
                            width: col.width || "auto",
                            minWidth: col.minWidth || "auto",
                            maxWidth: col.maxWidth || "none",
                          }}
                        >
                          {val == null || val === "" ? "-" : String(val)}
                        </td>
                      );
                    })}

                    {hasRowActions && (
                      <td className="dt-actions-cell">
                        <div className="dt-table-actions">
                          {(typeof rowActions === "function" ? rowActions(item) : rowActions).map((action, index) => {
                            const isRefund = action.label === "Refund";
                            const ButtonComponent = isRefund ? SecondButton : SecondButton;
                            return (
                              <ButtonComponent
                                key={`${action.label}-${index}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick(item);
                                }}
                                className="dt-table-action-btn"
                              >
                                {action.label}
                              </ButtonComponent>
                            );
                          })}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalRecords > pageSize && (
        <div className="dt-pagination-bar">
          <button
            className="dt-pagination-btn"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <span className="dt-pagination-center">
            Page {page} of {Math.ceil(totalRecords / pageSize)}
          </span>
          <button
            className="dt-pagination-btn"
            disabled={items.length < pageSize || page >= Math.ceil(totalRecords / pageSize)}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}

DataTable.propTypes = {
  fetchDataFunction: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  optionalColumns: PropTypes.array,
  sortOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  filterDefinitions: PropTypes.array,
  defaultSortBy: PropTypes.string,
  itemKey: PropTypes.string.isRequired,
  customRenderers: PropTypes.objectOf(PropTypes.func),
  rowActions: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        icon: PropTypes.string,
        onClick: PropTypes.func.isRequired,
      }),
    ),
    PropTypes.func,
  ]),
  actionsColumnLabel: PropTypes.string,
  onSelectionChange: PropTypes.func,
  showCheckboxes: PropTypes.bool,
  showFilters: PropTypes.bool,
  showPageSize: PropTypes.bool,
  defaultPageSize: PropTypes.number,
  tableId: PropTypes.string,
  emptyMessage: PropTypes.string,
};
